import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { JsonValue } from '@backstage/types';
import { useApi } from '@backstage/core-plugin-api';
import {
  Content,
  ErrorPanel,
  Header,
  Page,
  WarningPanel,
} from '@backstage/core-components';
import {
  SecretsContextProvider,
  scaffolderApiRef,
  useTemplateSecrets,
} from '@backstage/plugin-scaffolder-react';
import { Workflow } from '@backstage/plugin-scaffolder-react/alpha';
import { scaffolderFieldExtensions, scaffolderLayouts } from '../extensions';
import {
  decodeDraft,
  draftFromHash,
  parseTemplateRef,
  type ScaffolderDraft,
} from './draft';

/**
 * Renders any scaffolder template prefilled from a draft in the URL fragment.
 *
 * The handoff for "an agent worked out the answers, a human presses Create".
 * An MCP client can read a template's parameter schema and dry-run it, but it
 * cannot execute one: USER_OAUTH_TOKEN is minted by a browser OAuth popup and
 * supplied by the client as a task secret, and there is no headless equivalent.
 * So the agent gets the values right and hands them here, and the parts that
 * need a human — proving GitHub access, and deciding to publish — stay in the
 * browser.
 *
 * Deliberately template-agnostic: `templateRef` comes from the draft, so a
 * template added later works here the day it exists, with no change to this
 * file. The one thing it must do for itself is collect its own secrets through
 * a field extension (as new-internal-app does with GitHubOAuthField) rather
 * than relying on a bespoke wrapper page — this page has no template-specific
 * knowledge to fall back on.
 */
const DraftWorkflow = (props: { draft: ScaffolderDraft }) => {
  const { templateRef, values } = props.draft;
  const scaffolderApi = useApi(scaffolderApiRef);
  const navigate = useNavigate();

  // Whatever the template's own fields deposited. Passing these through is
  // what keeps the page generic: it never needs to know which secrets a given
  // template wants, only that its fields collected them.
  const { secrets } = useTemplateSecrets();

  const { namespace, name } = useMemo(
    () => parseTemplateRef(templateRef),
    [templateRef],
  );

  const onCreate = useCallback(
    async (formValues: Record<string, JsonValue>) => {
      const { taskId } = await scaffolderApi.scaffold({
        templateRef,
        values: formValues,
        secrets,
      });
      navigate(`/create/tasks/${taskId}`);
    },
    [scaffolderApi, templateRef, secrets, navigate],
  );

  // `Workflow`, not `EmbeddableWorkflow`: the embeddable one wraps itself in
  // its own SecretsContextProvider, so the fields would deposit their secrets
  // there and `secrets` above — read from the page's provider — would stay
  // `{}`. The task then runs without USER_OAUTH_TOKEN and the backend falls
  // back to its own GitHub App identity. This component must sit directly
  // under the same provider the fields write to.
  return (
    <Workflow
      namespace={namespace}
      templateName={name}
      initialState={values}
      extensions={scaffolderFieldExtensions}
      layouts={scaffolderLayouts}
      onCreate={onCreate}
      onError={e => (e ? <ErrorPanel error={e} /> : null)}
    />
  );
};

export const DraftCreatePage = () => {
  const location = useLocation();

  // Backstage's sign-in flow can round-trip through the identity provider,
  // and a fragment does not reliably survive that. Stash it on first sight so
  // the draft is still there when we land back here signed in.
  const [encoded, setEncoded] = useState<string | undefined>(() =>
    draftFromHash(location.hash),
  );
  useEffect(() => {
    const fromHash = draftFromHash(location.hash);
    if (fromHash) {
      setEncoded(fromHash);
      try {
        sessionStorage.setItem('scaffolder-draft', fromHash);
      } catch {
        // Private mode or blocked storage: the draft still works on this
        // render, it just will not survive a redirect.
      }
      return;
    }
    try {
      const stashed = sessionStorage.getItem('scaffolder-draft');
      if (stashed) setEncoded(stashed);
    } catch {
      // As above — nothing to recover, fall through to the empty state.
    }
  }, [location.hash]);

  // Decoding is async because gzip is: the browser only inflates through
  // `DecompressionStream`. It resolves in well under a frame, so there is no
  // spinner — `decoded` is simply undefined for the first render, which is the
  // same state the page is in before a draft arrives at all.
  // The `?: undefined` arms are what let the render below keep reading
  // `decoded?.draft` / `decoded?.error` directly: without them TypeScript
  // refuses either access on a bare union, since neither property exists on
  // both members.
  const [decoded, setDecoded] = useState<
    | { draft: ScaffolderDraft; error?: undefined }
    | { draft?: undefined; error: Error }
    | undefined
  >(undefined);

  useEffect(() => {
    if (!encoded) {
      setDecoded(undefined);
      return undefined;
    }
    let cancelled = false;
    decodeDraft(encoded)
      .then(draft => !cancelled && setDecoded({ draft }))
      .catch(
        e =>
          !cancelled &&
          setDecoded({ error: e instanceof Error ? e : new Error(String(e)) }),
      );
    // A second draft can arrive while the first is still inflating (the
    // sessionStorage fallback sets `encoded` a tick after the hash does), and
    // the slower one must not win the race and overwrite the newer draft.
    return () => {
      cancelled = true;
    };
  }, [encoded]);

  return (
    <Page themeId="tool">
      <Header
        title="Create from a draft"
        subtitle="Review what was prepared for you, then create it yourself"
      />
      <Content>
        {/*
          Keyed off `encoded`, not `decoded`: `decoded` is also undefined for
          the tick it takes to inflate a draft, and gating on it would flash
          "nothing to create" at someone who just opened a perfectly good link.
        */}
        {!encoded && (
          <WarningPanel
            severity="info"
            title="Nothing to create yet"
            message="This page opens a scaffolder template prefilled from a draft link. Open the link you were given, or start from the Create page instead."
          />
        )}
        {decoded?.error && (
          <ErrorPanel
            title="Couldn't read that draft link"
            error={decoded.error}
          />
        )}
        {decoded?.draft && (
          <SecretsContextProvider>
            <DraftWorkflow draft={decoded.draft} />
          </SecretsContextProvider>
        )}
      </Content>
    </Page>
  );
};
